import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { createMask } from '@ngneat/input-mask';
import { AuctionService } from '@services/auction/auction.service';
import { InventoryService } from '@services/inventory/inventory.service';
import { environment } from 'environments/environment';
import {
  IBatch,
  IInventoryBatchListResponse,
  IInventoryBatchProduct,
} from 'models/add-auction.model';
import { ViewerModalComponent } from 'ngx-ionic-image-viewer';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import {
  dateAndTimeConverter,
  daysDiff,
  sameDay,
  timesDiff,
} from '../../helpers/utils';
import { SalvageService } from '@services/salvage/salvage.service';

@Component({
  selector: 'app-create-salvage-auction',
  templateUrl: './create-salvage-auction.component.html',
  styleUrls: ['./create-salvage-auction.component.scss'],
})
export class CreateSalvageAuctionComponent implements OnInit, OnDestroy {
  @Input() activeQuery:
    | 'timed-individual'
    | 'individual'
    | 'bulk'
    | 'individual-batch'
    | null = null;
  @Input() entityType: string = '';
  @Input() loading: boolean = false;

  public BatchId = '';
  public auctionId = '';
  public productId: string = '';
  public isActiveAuction: boolean = false;
  public isBtnDisabled = true;
  public laddaLoading = false;
  public individualSubmitted = false;
  public auctionExists = false;
  public adminApprove = false;
  public lockStatus = false;
  public getBatchImagesForIndividual: IInventoryBatchProduct[] = [];
  public batch = {
    page: 1,
    limit: 20,
    loading: false,
  };
  public getProductImages: string[] = [];
  public currencyInputMask = createMask({
    alias: 'numeric',
    groupSeparator: ',',
    placeholder: '0',
    parser: (value: any) => {
      const parsedValue = value.replaceAll(',', '');
      return parsedValue;
    },
  });
  public batches: IBatch[] = [];
  public formBuilder = new FormBuilder();
  public individualBulkAuctionForm = this.formBuilder.group({
    auctionDetails: this.formBuilder.array([]),
  });
  public individualBatchAuctionForm = this.formBuilder.group({
    title: ['', [Validators.required]],
    item1: this.formBuilder.control(null),
    item2: [null],

    auction_start_date: ['', [Validators.required]], //
    auction_start_time: ['', [Validators.required]], //
    auction_end_date: ['', [Validators.required]], //
    auction_end_time: ['', [Validators.required]], //

    description: ['', [Validators.required]],

    live_auction_start_date: [
      { value: '', disabled: true },
      [Validators.required],
    ],
    live_auction_start_time: [
      { value: '', disabled: true },
      [Validators.required],
    ],
    live_auction_end_date: ['', [Validators.required]],
    live_auction_end_time: ['', [Validators.required]],

    opening_price: ['', [Validators.required]],
    lowest_price_to_accept: ['', [Validators.required]],
    incremental_value: ['', [Validators.required]],
    buy_now_price: ['', [Validators.required]],
    // time_interval: ['', [Validators.required, Validators.min(10)]],
    // countdown_timer: ['', [Validators.required, Validators.min(10)]],
    fields: this.formBuilder.array([]),
  });
  public isSubmited = false;
  public openBulkAuctionField = false;
  public subscription = new Subscription();

  // Below property are using to render error messages in dom conditionally
  public invalidIndividualOpeningPrice = false;
  public invalidIndividualLowestPriceToAccept = false;
  public invalidIndividualIncrementalValue = false;
  public invalidIndividualBuyNowPrice2 = false;
  public invalidIndividualBuyNowPrice3 = false;
  public baseUrl: string = environment.auctionBaseUrl;
  public invalidCommonCountdownTimer = false;
  public invalidCommonBiddingTime = false;
  public editId = '';
  public otherDetailsList: any[] = [];
  public allIndividualProductList: any[] = [];
  public initialVehicleList: object[] = [];
  public isThereNextPageOfAuctionVehicles: number;
  public ngSelectLoading: boolean;
  public notFoundText: string =
    'No vehicle found; try to search by VIN number.';
  private searchInputSubject = new Subject<{ items: any[]; term: string }>();
  public endAuctionLoading: boolean = false;

  constructor(
    public toastr: ToastrService,
    private inventoryService: InventoryService,
    public activeRoute: ActivatedRoute,
    private modalController: ModalController,
    public router: Router,
    public auctionService: AuctionService,
    public salvageService: SalvageService
  ) {
    const auctionId = this.activeRoute.snapshot.paramMap.get('auctionId');
    const query = this.activeRoute.snapshot.queryParams['type'];
    const auctionType = this.activeRoute.snapshot.queryParams['auctionType'];

    if (auctionId) {
      this.auctionId = auctionId;
    }
    if (query) {
      this.activeQuery = query;
    }
    if (auctionType) {
      this.entityType = auctionType;
    }

    this.searchInputSubject.pipe(debounceTime(2000)).subscribe((event) => {
      this.onSearch(event);
    });

    this.individualBatchAuctionForm
      .get('auction_end_date')
      .valueChanges.subscribe((endDate) => {
        this.individualBatchAuctionForm.patchValue({
          live_auction_start_date: endDate,
        });
      });

    this.individualBatchAuctionForm
      .get('auction_end_time')
      .valueChanges.subscribe((endTime) => {
        this.individualBatchAuctionForm.patchValue({
          live_auction_start_time: endTime,
        });
      });
  }

  ngOnInit(): void {
    this.getAllBatchList();
    this.getAllListedproducts(1);

    if (this.auctionId) {
      this.getEditAuctionInfo();
      this.individualBatchAuctionForm.controls['item1'].disable();
      this.individualBatchAuctionForm.controls['item2'].disable();
    }
  }

  get newFields(): FormGroup {
    return this.formBuilder.group({
      entity_name: [''],
      entity_value: [''],
    });
  }

  get individualBatchFormArray(): FormArray & { controls: FormGroup[] } {
    return this.individualBatchAuctionForm.get('fields') as FormArray & {
      controls: FormGroup[];
    };
  }

  get individualBukFormArray(): FormArray & { controls: FormGroup[] } {
    return this.individualBulkAuctionForm.get('auctionDetails') as FormArray & {
      controls: FormGroup[];
    };
  }

  get IndividualBulkFields(): FormGroup {
    return this.formBuilder.group({
      openingPrice: [''],
      lowestPriceToAccept: [''],
      incrementalValue: [''],
      buyNowPrice: [''],
      itemId: [''],
    });
  }

  get IndividualBatchControl() {
    return this.individualBatchAuctionForm.controls;
  }

  async openViewer(imageURL: string) {
    const modal = await this.modalController.create({
      component: ViewerModalComponent,
      componentProps: {
        src: imageURL,
        scheme: 'dark',
      },
      cssClass: 'ion-img-viewer modal-popup modal-popup-container',
      keyboardClose: true,
      showBackdrop: true,
    });

    return await modal.present();
  }

  // Method run in every kepup event in price input section for every create-auction page
  runValidator(event: KeyboardEvent, type?: string) {
    switch ((event?.target as HTMLInputElement)?.['name']) {
      case 'opening_price':
        {
          const openingPrice = parseInt(
            this.individualBatchAuctionForm.get('opening_price')?.value
          );
          if (openingPrice < 1) {
            this.invalidIndividualOpeningPrice = true;
          } else {
            this.invalidIndividualOpeningPrice = false;
          }
        }
        break;

      case 'lowest_price_to_accept':
        {
          const openingPrice = parseInt(
            this.individualBatchAuctionForm.get('opening_price')?.value
          );
          const lowestPriceToAccept = parseInt(
            this.individualBatchAuctionForm.get('lowest_price_to_accept')?.value
          );

          if (lowestPriceToAccept < openingPrice) {
            this.invalidIndividualLowestPriceToAccept = true;
          } else {
            this.invalidIndividualLowestPriceToAccept = false;
          }
        }
        break;

      case 'incremental_value':
        {
          const incrementalValue = parseInt(
            this.individualBatchAuctionForm.get('incremental_value')?.value
          );
          if (incrementalValue < 1) {
            this.invalidIndividualIncrementalValue = true;
          } else {
            this.invalidIndividualIncrementalValue = false;
          }
        }
        break;

      case 'buy_now_price':
        {
          const buyNowPrice = parseInt(
            this.individualBatchAuctionForm.get('buy_now_price')?.value
          );
          const incrementalValue = parseInt(
            this.individualBatchAuctionForm.get('incremental_value')?.value
          );
          const lowestPriceToAccept = parseInt(
            this.individualBatchAuctionForm.get('lowest_price_to_accept')?.value
          );

          if (incrementalValue > buyNowPrice) {
            this.invalidIndividualBuyNowPrice2 = true;
          } else {
            this.invalidIndividualBuyNowPrice2 = false;
          }

          if (lowestPriceToAccept > buyNowPrice) {
            this.invalidIndividualBuyNowPrice3 = true;
          } else {
            this.invalidIndividualBuyNowPrice3 = false;
          }
        }
        break;

      case 'time_interval':
        {
          let biddingInterval;

          biddingInterval = parseInt(
            this.individualBatchAuctionForm.get('time_interval')?.value
          );

          if (biddingInterval >= 80) {
            this.invalidCommonBiddingTime = false;
          } else {
            this.invalidCommonBiddingTime = true;
          }
        }
        break;

      case 'countdown_timer':
        {
          let biddingInterval;
          let countdownTimer;

          biddingInterval = parseInt(
            this.individualBatchAuctionForm.get('time_interval')?.value
          );
          countdownTimer = parseInt(
            this.individualBatchAuctionForm.get('countdown_timer')?.value
          );

          if (
            countdownTimer > biddingInterval ||
            countdownTimer === 0 ||
            countdownTimer < 30
          ) {
            this.invalidCommonCountdownTimer = true;
          } else {
            this.invalidCommonCountdownTimer = false;
          }
        }
        break;
    }
  }

  getEditAuctionInfo() {
    this.loading = true;

    this.auctionService.getEditAuctionData(this.auctionId).subscribe(
      (res) => {
        this.isBtnDisabled = false;
        this.productId = res['data']['product_id'];
        this.isActiveAuction = res['data']['active_time_status'];
        this.lockStatus = res['data']['lock_status'];
        this.adminApprove = res['data']?.['approval'];
        let data = res['data'];
        this.individualBatchAuctionForm.get('title')?.setValue(data['title']);
        this.individualBatchAuctionForm
          .get('opening_price')
          ?.setValue(data['opening_price']);
        this.individualBatchAuctionForm
          .get('lowest_price_to_accept')
          ?.setValue(data['lowest_price']);
        this.individualBatchAuctionForm
          .get('incremental_value')
          ?.setValue(data['incremental_price']);
        this.individualBatchAuctionForm
          .get('buy_now_price')
          ?.setValue(data['buy_now_price']);

        // Set Auction Values

        let auction_start_dates = data['auction_start_date_time'].split(',')[0];
        let auction_start_times =
          data['auction_start_date_time'].split(',')[1].slice(1, 6) +
          data['auction_start_date_time'].split(',')[1].slice(9, 12);

        this.individualBatchAuctionForm
          .get('auction_start_date')
          ?.setValue(auction_start_dates);
        this.individualBatchAuctionForm
          .get('auction_start_time')
          ?.setValue(auction_start_times);

        let auction_end_dates = data['auction_end_date_time'].split(',')[0];
        let auction_end_times =
          data['auction_end_date_time'].split(',')[1].slice(1, 6) +
          data['auction_end_date_time'].split(',')[1].slice(9, 12);

        this.individualBatchAuctionForm
          .get('auction_end_date')
          ?.setValue(auction_end_dates);
        this.individualBatchAuctionForm
          .get('auction_end_time')
          ?.setValue(auction_end_times);

        // END

        if (
          // data['auction_type'] !== '0' &&
          data['entity_type'] === 'salvage'
        ) {
          let description_data = data['description'];
          this.individualBatchAuctionForm
            .get('description')
            ?.setValue(description_data);
        }

        // Set Live Auction Values

        let live_auction_start_dates =
          data['live_auction_start_date_time'].split(',')[0];
        let live_auction_start_times =
          data['live_auction_start_date_time'].split(',')[1].slice(1, 6) +
          data['live_auction_start_date_time'].split(',')[1].slice(9, 12);

        this.individualBatchAuctionForm
          .get('live_auction_start_date')
          ?.setValue(live_auction_start_dates);
        this.individualBatchAuctionForm
          .get('live_auction_start_time')
          ?.setValue(live_auction_start_times);

        let live_auction_end_dates =
          data['live_auction_end_date_time'].split(',')[0];
        let live_auction_end_times =
          data['live_auction_end_date_time'].split(',')[1].slice(1, 6) +
          data['live_auction_end_date_time'].split(',')[1].slice(9, 12);

        this.individualBatchAuctionForm
          .get('live_auction_end_date')
          ?.setValue(live_auction_end_dates);
        this.individualBatchAuctionForm
          .get('live_auction_end_time')
          ?.setValue(live_auction_end_times);

        this.individualBatchAuctionForm
          .get('time_interval')
          ?.setValue(data['bidding_time_interval']);
        this.individualBatchAuctionForm
          .get('countdown_timer')
          ?.setValue(data['countdown_timer']);

        let item = `${data?.['description_data']?.['Model Year']} ${data?.['description_data']?.['Model']} ${data?.['description_data']?.['Interior Colour']} (${data?.['description_data']?.['VIN Number']})`;
        this.individualBatchAuctionForm.get('item1')?.setValue(item);
        this.editId = data['product_id'];
        // END

        //Set Image
        this.getProductImages = [];
        if (data['imageUrl'] && !data['vehicle_images']) {
          for (let [item, images] of Object.entries(
            data['imageUrl'] as Record<string, any>
          )) {
            this.getProductImages.push(images['presigned_url']);
          }
        } else if (data['vehicle_images'] && !data['imageUrl']) {
          for (let [item, images] of Object.entries(
            data['vehicle_images']['parts_images'] as Record<string, any>
          )) {
            this.getProductImages.push(this.baseUrl + images['image']);
          }
        }
        //END

        // Set Others fields Values
        this.otherDetailsList = [];
        this.otherDetailsList = data['other_details'];
        for (let [index, value] of Object.entries(this.otherDetailsList)) {
          this.individualBatchFormArray.push(
            this.formBuilder.group({
              entity_name: value['entity_name'],
              entity_value: value['entity_value'],
            })
          );
        }
        // END
        this.loading = false;
      },
      (error: HttpErrorResponse) => {
        if (error.error.message == 'The incoming token has expired') {
          this.router.navigate(['/']);
        } else {
          this.toastr.warning(error.statusText);
        }
      }
    );
  }

  getAllListedproducts(page: number) {
    if (page === 1) {
      this.loading = true;
    } else if (page > 1) {
      this.ngSelectLoading = true;
    }

    this.inventoryService.getAllAuctionVehicle(1, 0, page).subscribe(
      (res) => {
        const data = res['data'];
        if (Array.isArray(data)) {
          res['data'].forEach((item) => {
            const details = item['description_data'];
            if (item['description_data']?.['VIN Number']) {
              this.allIndividualProductList.push({
                ...item,
                name: `${details['Model Year']} ${details['Model']} ${details['Interior Colour']} (${details['VIN Number']})`,
              });
            }
          });
          this.allIndividualProductList = this.allIndividualProductList.sort(
            (a, b) => a.name.localeCompare(b.name)
          );

          this.initialVehicleList = this.allIndividualProductList;
        }
        this.loading = false;
        this.ngSelectLoading = false;
        this.isThereNextPageOfAuctionVehicles = res?.['nextPage'];
      },
      (error: HttpErrorResponse) => {
        if (error.error.message == 'The incoming token has expired') {
          this.router.navigate(['/']);
        }
      }
    );
  }

  fetchNextPage() {
    if (!this.isThereNextPageOfAuctionVehicles) {
      return;
    }

    this.getAllListedproducts(this.isThereNextPageOfAuctionVehicles);
  }

  onSearchInput(event: { items: any[]; term: string }) {
    if (!event.term) {
      this.allIndividualProductList = this.initialVehicleList;
      return;
    }

    this.searchInputSubject.next(event);
  }

  onSearch(event: { items: any[]; term: string }) {
    if (event.items.length > 0) {
      return;
    }

    this.ngSelectLoading = true;

    this.inventoryService
      .filterProduct({ 'VIN Number': event.term.trim() })
      .subscribe((res) => {
        if (res?.['data']?.length) {
          this.allIndividualProductList = [];

          res['data'].forEach((item) => {
            const details = item['description_data'];
            if (item['description_data']?.['VIN Number']) {
              this.allIndividualProductList.push({
                ...item,
                name: `${details['Model Year']} ${details['Model']} ${details['Interior Colour']} (${details['VIN Number']})`,
              });
            }
          });
          this.allIndividualProductList = this.allIndividualProductList.sort(
            (a, b) => a.name.localeCompare(b.name)
          );
        } else {
          this.notFoundText = 'No vehicle found';
        }

        this.ngSelectLoading = false;
      });
  }

  getProductInfo(event: string, value: boolean) {
    this.individualBatchAuctionForm.get('item2')?.setValue('');
    this.individualBatchAuctionForm.get('auction_start_date')?.setValue('');
    this.individualBatchAuctionForm.get('auction_start_time')?.setValue('');
    this.individualBatchAuctionForm.get('auction_end_date')?.setValue('');
    this.individualBatchAuctionForm.get('auction_end_time')?.setValue('');
    this.individualBatchAuctionForm
      .get('live_auction_start_date')
      ?.setValue('');
    this.individualBatchAuctionForm
      .get('live_auction_start_time')
      ?.setValue('');
    this.individualBatchAuctionForm.get('live_auction_end_date')?.setValue('');
    this.individualBatchAuctionForm.get('live_auction_end_time')?.setValue('');
    this.individualBatchAuctionForm.get('description')?.setValue('');

    this.openBulkAuctionField = value;
    this.getProductImages = [];
    this.inventoryService.getvehicleInfo(event).subscribe(
      (res) => {
        const data = res['data'];
        const description = data['description_data']['description'];
        this.individualBatchAuctionForm
          .get('description')
          ?.setValue(description);

        if (data['imageUrl'] && !data['vehicle_images']) {
          for (const images of Object.values(data['imageUrl'])) {
            this.getProductImages.push((images as any)['presigned_url']);
          }
        } else if (data['vehicle_images']) {
          for (const images of Object.values(
            data['vehicle_images']['parts_images']
          )) {
            this.getProductImages.push(this.baseUrl + (images as any)['image']);
          }
        }
      },
      (error: HttpErrorResponse) => {
        if (error.error.message == 'The incoming token has expired') {
          this.router.navigate(['/']);
        }
      }
    );
  }

  getAllBatchList() {
    this.batch.loading = true;

    const sub = this.inventoryService
      .fetchBatches<IInventoryBatchListResponse>(
        this.batch.page,
        this.batch.limit
      )
      .subscribe({
        next: (res) => {
          if (res?.['success']) {
            const newItems = res['data'] || [];
            this.batches = [...this.batches, ...newItems];
            this.batch.page = res?.['nextPage'] ?? -1;
            this.batch.loading = false;
          }
        },
        error: (error) => {
          this.batch.loading = false;
          console.error(error);
        },
      });

    this.subscription.add(sub);
  }

  getbatchInfoForIndividual(event: string, value: any, type: string) {
    this.entityType = type;
    this.individualBatchAuctionForm.get('item1')?.setValue('');
    this.individualBatchAuctionForm.get('auction_start_date')?.setValue('');
    this.individualBatchAuctionForm.get('auction_start_time')?.setValue('');
    this.individualBatchAuctionForm.get('auction_end_date')?.setValue('');
    this.individualBatchAuctionForm.get('auction_end_time')?.setValue('');
    this.individualBatchAuctionForm
      .get('live_auction_start_date')
      ?.setValue('');
    this.individualBatchAuctionForm
      .get('live_auction_start_time')
      ?.setValue('');
    this.individualBatchAuctionForm.get('live_auction_end_date')?.setValue('');
    this.individualBatchAuctionForm.get('live_auction_end_time')?.setValue('');
    this.individualBatchAuctionForm.get('description')?.setValue('');
    this.individualBatchAuctionForm.get('time_interval')?.setValue('');
    this.individualBatchAuctionForm.get('countdown_timer')?.setValue('');

    this.openBulkAuctionField = value;
    this.getBatchImagesForIndividual = [];
    this.individualBukFormArray.clear();
    this.BatchId = event;
    this.inventoryService.getBatchView(this.BatchId).subscribe(
      (res) => {
        this.individualBatchAuctionForm
          .get('description')
          ?.setValue(res?.['data']?.[0]?.['description']);
        res['data'].shift();

        for (let product of res['data']) {
          if (product?.['auction_id']) {
            this.individualBukFormArray.clear();
            this.toastr.warning('Auction already exists!');
            this.auctionExists = true;
            break;
          } else {
            this.auctionExists = false;
            this.individualBukFormArray.push(this.IndividualBulkFields);
            this.getBatchImagesForIndividual.push(product);
          }
        }
      },
      (error: HttpErrorResponse) => {
        this.laddaLoading = false;
      }
    );
  }

  saveIndividualBatchAuction() {
    this.isSubmited = true;

    if (this.entityType === 'individual_batch') {
      this.individualBatchAuctionForm.controls['opening_price'].setErrors(null);
      this.individualBatchAuctionForm.controls[
        'lowest_price_to_accept'
      ].setErrors(null);
      this.individualBatchAuctionForm.controls['incremental_value'].setErrors(
        null
      );
      this.individualBatchAuctionForm.controls['buy_now_price'].setErrors(null);
    }

    if (
      this.individualBatchAuctionForm.invalid ||
      (!this.individualBatchAuctionForm.controls['item1'].value &&
        !this.individualBatchAuctionForm.controls['item2'].value)
    ) {
      this.toastr.error('Invalid Form');
      return;
    }

    const data = this.individualBatchAuctionForm.getRawValue();
    const auctionStartDate = data['auction_start_date'];
    const auctionEndDate = data['auction_end_date'];
    const liveAuctionStartDate = data['live_auction_start_date'];
    const liveAuctionEndDate = data['live_auction_end_date'];

    if (!daysDiff(auctionStartDate, auctionEndDate)) {
      return this.toastr.error('Auction end-date can not be before start-date');
    }
    if (
      sameDay(auctionStartDate, auctionEndDate) &&
      (!timesDiff(data['auction_start_time'], data['auction_end_time']) ||
        timesDiff(
          data['auction_start_time'],
          data['auction_end_time']
        ).includes('-') ||
        timesDiff(data['auction_start_time'], data['auction_end_time']) ===
          '00:00')
    ) {
      return this.toastr.error(
        'Please choose End time greater than Start time!'
      );
    }
    if (!daysDiff(liveAuctionStartDate, liveAuctionEndDate)) {
      return this.toastr.error(
        'Live auction end-date can not be before start-date'
      );
    }
    const liveAuctionStart = dateAndTimeConverter(
      liveAuctionStartDate,
      data['live_auction_start_time']
    );
    const liveAuctionEnd = dateAndTimeConverter(
      liveAuctionEndDate,
      data['live_auction_end_time']
    );
    if (
      liveAuctionStart &&
      liveAuctionEnd &&
      new Date(liveAuctionEnd).getTime() <= new Date(liveAuctionStart).getTime()
    ) {
      return this.toastr.error(
        'Live auction end date and time must be greater than Live auction the start date and time'
      );
    }

    const biddingTimeInterval = Number(data['time_interval']);
    const countdownTimer = Number(data['countdown_timer']);
    if (biddingTimeInterval < countdownTimer) {
      return this.toastr.error(
        'Bidding time interval should be greater than countdown timer'
      );
    }

    const payload = {
      auction_id: this.auctionId,
      title: data['title'],
      entity_type: this.entityType,
      item: this.productId,
      auction_start_date_time: dateAndTimeConverter(
        auctionStartDate,
        data['auction_start_time']
      ),
      auction_end_date_time: dateAndTimeConverter(
        auctionEndDate,
        data['auction_end_time']
      ),
      live_auction_start_date_time: dateAndTimeConverter(
        liveAuctionStartDate,
        data['live_auction_start_time']
      ),
      live_auction_end_date_time: dateAndTimeConverter(
        liveAuctionEndDate,
        data['live_auction_end_time']
      ),
      description: data['description'],
      // price,
      opening_price: data['opening_price'],
      lowest_price: data['lowest_price_to_accept'],
      incremental_price: data['incremental_value'],
      buy_now_price: data['buy_now_price'],
      other_details: data['fields'],
      auto_bid_setup: 0,
      auction_type: null,
      // countdown_timer: countdownTimer,
      // bidding_time_interval: biddingTimeInterval,
      auction_time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    this.laddaLoading = true;
    this.salvageService.editAuction(payload).subscribe({
      next: (response: any) => {
        this.toastr.success(response?.message || 'Auction saved successfully');
        this.router.navigate(['/dashboard/auction/salvage/'], {
          queryParams: { tab: 'inventory', page: 1, limit: 10 },
        });
      },
      error: () => {
        this.laddaLoading = false;
      },
    });
  }

  addFields() {
    this.individualBatchFormArray.push(this.newFields);
  }

  removeFields(index: number) {
    if (typeof index === 'number')
      this.individualBatchFormArray.removeAt(index);
  }

  onEndAuction() {
    this.endAuctionLoading = true;
    this.auctionService.endAuction(this.auctionId).subscribe(
      (res) => {
        this.toastr.success(res['message']);
        this.router.navigate([`dashboard/auction/salvage`], {
          queryParams: {
            tab: 'on-going',
            page: 1,
            limit: 10,
          },
        });
        this.endAuctionLoading = false;
      },
      (error: HttpErrorResponse) => {
        this.laddaLoading = false;
        console.log(error);
      }
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
